import Link from 'next/link'
import React, { useState } from 'react'
import { FaCaretDown, FaWhatsapp } from 'react-icons/fa'
import { whatsAppDisplay, whatsAppUrl } from 'utils/business-info'

/**
 * Desktop nav building blocks, shared by IndexNav and every segment navbar
 * (aircon, handyman, trainer, helper) so all desktop menu items look and
 * behave the same.
 *
 * Every dropdown opens on hover, and on click/keyboard focus (the trigger is a
 * button, and focus-within keeps the panel open). The panel is revealed with
 * CSS rather than mounted by JS, so the links are in the server-rendered HTML
 * that crawlers see. The segment menus used to be Headless UI menus that only
 * opened on click and only rendered their links once opened.
 *
 * Mobile menus (the *NavBarSmall components) are separate and unchanged.
 */

const triggerClass = 'flex items-center gap-1 px-2 py-2 text-orange-500 hover:text-orange-400 group-hover:text-orange-400 focus:text-orange-400 focus:outline-none'

const revealClass = 'group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100'

export const NavDropdown = ({ label, items }) => {
    // The navbar stays mounted across client-side navigations, so after a link
    // is clicked the pointer is still hovering the panel and the link still has
    // focus - the CSS reveal would keep the menu open on the new page. Clicking
    // dismisses it until the pointer leaves or focus re-enters the menu.
    const [dismissed, setDismissed] = useState(false)

    const handleItemClick = (e) => {
        e.currentTarget.blur()
        setDismissed(true)
    }

    return (
        <li className="relative group"
            onMouseLeave={() => setDismissed(false)}
            onFocus={() => setDismissed(false)}>
            <button type="button" aria-haspopup="true" className={triggerClass}>
                <strong className="flex items-center">
                    {label}
                    <FaCaretDown className="h-4 w-4 ml-0.5" aria-hidden="true" />
                </strong>
            </button>
            <ul
                aria-label={label}
                className={`absolute left-0 top-full z-50 w-64 py-2 bg-white rounded-md shadow-lg ring-1 ring-black/5 invisible opacity-0 ${dismissed ? '' : revealClass} transition-opacity duration-150`}
            >
                {items.map(({ href, label: itemLabel }) => (
                    <li key={href}>
                        <Link href={href} prefetch={false} onClick={handleItemClick}
                            className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600 focus:bg-orange-50 focus:text-orange-600 focus:outline-none">
                            {itemLabel}
                        </Link>
                    </li>
                ))}
            </ul>
        </li>
    )
}

export const NavLinkItem = ({ href, label, ariaLabel, current = false }) => (
    <li>
        <Link href={href} prefetch={false} className={triggerClass} aria-label={ariaLabel}
            aria-current={current ? 'page' : undefined}>
            <strong>{label}</strong>
        </Link>
    </li>
)

export const NavWhatsApp = ({ text, ariaLabel }) => (
    <li>
        {/* Primary contact CTA - server rendered so the number is crawlable */}
        <a
            href={`${whatsAppUrl}?text=${encodeURIComponent(text)}`}
            target="_blank"
            rel="noreferrer"
            aria-label={ariaLabel}
            className="flex items-center gap-2 bg-linear-to-tr from-green-700 to-green-500 text-white text-sm font-bold px-5 py-2.5 rounded-full shadow-lg"
        >
            <FaWhatsapp className="text-base" aria-hidden="true" />
            {whatsAppDisplay}
        </a>
    </li>
)
